/**
 * Limits and user-facing messages for files attached to an AI chat turn.
 * The caps mirror the server's `config('ai.assistant.attachments')`; the
 * server re-checks them, these exist so people learn the limits BEFORE they
 * pick a file and get a message that names the limit when they hit one.
 */

export const AI_ATTACHMENT_LIMITS = {
  maxFiles: 4,
  maxFileBytes: 2 * 1024 * 1024,
  maxTotalBytes: 6 * 1024 * 1024,
  // No GIF: Gemini does not take it, and the server refuses it.
  mimes: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'],
} as const;

const TYPE_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WEBP',
  'application/pdf': 'PDF',
};

export const AI_ATTACHMENT_TOO_LARGE_FOR_SERVER =
  'The file was too large for the server to accept. Try a smaller file, a screenshot of the page you need, or send the message without it.';

export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function wholeMb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function typeList(): string {
  const labels = AI_ATTACHMENT_LIMITS.mimes.map((m) => TYPE_LABELS[m] ?? m);
  return labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`
    : labels.join('');
}

/** e.g. "Up to 4 files · 2 MB each · 6 MB total · PNG, JPEG, WEBP or PDF" */
export function describeAttachmentLimits(): string {
  const l = AI_ATTACHMENT_LIMITS;
  return `Up to ${l.maxFiles} files · ${wholeMb(l.maxFileBytes)} each · ${wholeMb(l.maxTotalBytes)} total · ${typeList()}`;
}

export interface AttachmentCandidate {
  name: string;
  size: number;
  type: string;
}

/**
 * Why `file` can't join the files already staged, or null when it can.
 * `stop` means no later file in the same pick can fit either.
 */
export function attachmentRefusal(
  file: AttachmentCandidate,
  staged: { size: number }[],
): { message: string; stop: boolean } | null {
  const l = AI_ATTACHMENT_LIMITS;
  if (staged.length >= l.maxFiles) {
    return { message: `You can attach at most ${l.maxFiles} files to one message.`, stop: true };
  }
  if (!(l.mimes as readonly string[]).includes(file.type)) {
    return { message: `"${file.name}" is not a supported type — ${typeList()} only.`, stop: false };
  }
  if (file.size > l.maxFileBytes) {
    return {
      message: `"${file.name}" is ${formatBytes(file.size)} — each file can be at most ${wholeMb(l.maxFileBytes)}.`,
      stop: false,
    };
  }
  const total = staged.reduce((sum, f) => sum + f.size, 0) + file.size;
  if (total > l.maxTotalBytes) {
    return {
      message: `Adding "${file.name}" would make ${formatBytes(total)} — attachments can be at most ${wholeMb(l.maxTotalBytes)} in total.`,
      stop: true,
    };
  }
  return null;
}

/**
 * True when a failed send most likely died because the request was too big:
 * an HTTP 413, or the bare network failure a browser reports when a proxy
 * rejects the body without CORS headers ("Failed to fetch" / "Load failed").
 */
export function isOversizeSendFailure(e: unknown, status?: number): boolean {
  if (status === 413) return true;
  if (!(e instanceof TypeError)) return false;
  return /failed to fetch|load failed|networkerror/i.test(e.message);
}
